import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Bell, BellOff, UserPlus, Ruler, AlertTriangle, ClipboardCheck, Calendar, FileText, MessageSquare, TrendingUp, Loader2, Save, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

const NOTIFICATION_TYPES = [
  {
    key: 'new_athlete',
    label: 'New Athlete Added',
    description: 'When a new athlete profile is created',
    icon: UserPlus,
    color: 'text-[#02b5ff]'
  },
  {
    key: 'measurement_added',
    label: 'Measurement Updates',
    description: 'When height, weight, or other measurements are recorded',
    icon: Ruler,
    color: 'text-green-400'
  },
  {
    key: 'bmi_alert',
    label: 'BMI Health Alerts',
    description: 'When BMI is outside healthy range (critical)',
    icon: AlertTriangle,
    color: 'text-[#ff025f]'
  },
  {
    key: 'assessment_completed',
    label: 'Assessment Completed',
    description: 'When SMHAT or other assessments are finished',
    icon: ClipboardCheck,
    color: 'text-purple-400'
  },
  {
    key: 'booking_created',
    label: 'New Bookings',
    description: 'When a testing session is booked',
    icon: Calendar,
    color: 'text-[#02b5ff]'
  },
  {
    key: 'booking_cancelled',
    label: 'Booking Cancellations',
    description: 'When a session is cancelled',
    icon: Calendar,
    color: 'text-zinc-400'
  },
  {
    key: 'document_uploaded',
    label: 'Document Uploads',
    description: 'When athletes upload documents for review',
    icon: FileText,
    color: 'text-yellow-400'
  },
  {
    key: 'coach_comment',
    label: 'Coach Comments',
    description: 'When a coach comments on a document',
    icon: MessageSquare,
    color: 'text-[#02b5ff]'
  },
  {
    key: 'growth_concern',
    label: 'Growth Concerns',
    description: 'When unusual growth patterns are detected (critical)',
    icon: TrendingUp,
    color: 'text-orange-400'
  }
];

export default function NotificationSettings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await axios.get(`${API}/notifications/preferences`);
      setPreferences(response.data);
    } catch (error) {
      console.error('Failed to load preferences:', error);
      // Set defaults
      const defaults = {};
      NOTIFICATION_TYPES.forEach(type => {
        defaults[type.key] = true;
      });
      setPreferences(defaults);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notifications/preferences`, preferences);
      toast.success('Notification preferences saved!');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const enableAll = () => {
    const newPrefs = {};
    NOTIFICATION_TYPES.forEach(type => {
      newPrefs[type.key] = true;
    });
    setPreferences(newPrefs);
    setHasChanges(true);
  };

  const disableAll = () => {
    const newPrefs = {};
    NOTIFICATION_TYPES.forEach(type => {
      newPrefs[type.key] = false;
    });
    setPreferences(newPrefs);
    setHasChanges(true);
  };

  const enabledCount = Object.values(preferences).filter(Boolean).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#02b5ff] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-8" data-testid="notification-settings-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-zinc-400 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Notification Settings</h1>
            <p className="text-zinc-500 text-sm">Manage your email notification preferences</p>
          </div>
        </div>

        {/* Summary Bar */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${enabledCount > 0 ? 'bg-[#02b5ff]/20' : 'bg-zinc-800'}`}>
              {enabledCount > 0 ? (
                <Bell className="h-5 w-5 text-[#02b5ff]" />
              ) : (
                <BellOff className="h-5 w-5 text-zinc-500" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium">{enabledCount} of {NOTIFICATION_TYPES.length} notifications enabled</p>
              <p className="text-xs text-zinc-500">Receiving emails to {user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={enableAll}
              className="text-xs border-zinc-700"
            >
              Enable All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={disableAll}
              className="text-xs border-zinc-700"
            >
              Disable All
            </Button>
          </div>
        </div>

        {/* Notification Types */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
          {NOTIFICATION_TYPES.map((type, index) => {
            const Icon = type.icon;
            const isEnabled = preferences[type.key] ?? true;
            const isCritical = type.key === 'bmi_alert' || type.key === 'growth_concern';

            return (
              <div
                key={type.key}
                className={`p-4 flex items-center justify-between ${
                  index !== NOTIFICATION_TYPES.length - 1 ? 'border-b border-zinc-800' : ''
                }`}
                data-testid={`notification-${type.key}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${isEnabled ? 'bg-zinc-800' : 'bg-zinc-900'}`}>
                    <Icon className={`h-5 w-5 ${isEnabled ? type.color : 'text-zinc-600'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className={`font-medium ${isEnabled ? 'text-white' : 'text-zinc-500'}`}>
                        {type.label}
                      </p>
                      {isCritical && (
                        <span className="text-[10px] bg-[#ff025f]/20 text-[#ff025f] px-2 py-0.5 rounded-full">
                          CRITICAL
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500">{type.description}</p>
                  </div>
                </div>
                <Switch
                  checked={isEnabled}
                  onCheckedChange={() => handleToggle(type.key)}
                  data-testid={`toggle-${type.key}`}
                />
              </div>
            );
          })}
        </div>

        {/* Save Button */}
        {hasChanges && (
          <div className="fixed bottom-0 left-0 right-0 bg-zinc-900/95 backdrop-blur-sm border-t border-zinc-800 p-4">
            <div className="max-w-2xl mx-auto flex items-center justify-between">
              <p className="text-sm text-zinc-400">You have unsaved changes</p>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#02b5ff] hover:bg-[#0099dd] text-white"
                data-testid="save-preferences-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Preferences
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-6 bg-zinc-900/30 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-white">Email notifications</p>
              <p className="text-xs text-zinc-500 mt-1">
                Notifications are sent to your registered email address. Critical alerts (BMI and Growth Concerns) 
                are recommended to keep enabled for athlete safety.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
